import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IntentionDto, MAX_INTENTIONS } from '@ivy-monorepo/shared'
import Icon from '../Icon'

type IntentionsSortableTableProps = {
  intentions: IntentionDto[]
  activeCount: number
  isSaving?: boolean
  togglingPublicId?: string | null
  onReorder: (intentions: IntentionDto[]) => void
  onRowClick: (intention: IntentionDto) => void
  onActiveChange: (intention: IntentionDto, isActive: boolean) => void
}

type SortableRowProps = {
  intention: IntentionDto
  position: number
  activeCount: number
  isDisabled?: boolean
  isToggling?: boolean
  onRowClick: (intention: IntentionDto) => void
  onActiveChange: (intention: IntentionDto, isActive: boolean) => void
}

const formatDateTime = (value?: string) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const truncateText = (text: string, maxLength = 80) => {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 1)}…`
}

const IntentionRowContent = ({
  intention,
  position,
  activeCount,
  isDisabled = false,
  isToggling = false,
  dragHandleProps,
  onRowClick,
  onActiveChange,
  isOverlay = false,
}: SortableRowProps & {
  dragHandleProps?: Record<string, unknown>
  isOverlay?: boolean
}) => {
  const isActive = intention.isActive !== false
  const canActivate = isActive || activeCount < MAX_INTENTIONS

  return (
  <>
    <td className="map-filter-drag-cell">
      <button
        type="button"
        className="map-filter-drag-handle"
        aria-label={`Drag to reorder ${intention.name}`}
        disabled={isOverlay}
        {...dragHandleProps}
      >
        <Icon name="GripVertical" className="map-filter-drag-icon" />
        <span className="map-filter-position-label">{position + 1}</span>
      </button>
    </td>
    <td className="intention-emoji-cell">{intention.emoji}</td>
    <td>{intention.name}</td>
    <td className="intention-prompt-cell" title={intention.prompt}>
      {truncateText(intention.prompt)}
    </td>
    <td className="intention-prompt-cell" title={intention.description ?? ''}>
      {intention.description ? truncateText(intention.description) : '—'}
    </td>
    <td className="intention-active-cell">
      <label className="intention-active-toggle">
        <input
          type="checkbox"
          checked={isActive}
          disabled={isOverlay || isDisabled || isToggling || !canActivate}
          onChange={event => onActiveChange(intention, event.target.checked)}
          onClick={event => event.stopPropagation()}
          aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${intention.name}`}
        />
        <span>Active</span>
      </label>
    </td>
    <td>{formatDateTime(intention.updatedAt)}</td>
    {!isOverlay && (
      <td className="intention-row-action-cell">
        <button
          type="button"
          className="styled-button sm"
          onClick={() => onRowClick(intention)}
        >
          Edit
        </button>
      </td>
    )}
  </>
  )
}

const SortableIntentionRow = ({
  intention,
  position,
  activeCount,
  isDisabled,
  isToggling,
  onRowClick,
  onActiveChange,
}: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: intention.publicId,
  })

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'map-filter-row-dragging' : undefined}
    >
      <IntentionRowContent
        intention={intention}
        position={position}
        activeCount={activeCount}
        isDisabled={isDisabled}
        isToggling={isToggling}
        onRowClick={onRowClick}
        onActiveChange={onActiveChange}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </tr>
  )
}

const IntentionsSortableTable = ({
  intentions,
  activeCount,
  isSaving = false,
  togglingPublicId = null,
  onReorder,
  onRowClick,
  onActiveChange,
}: IntentionsSortableTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableWidth, setTableWidth] = useState<number>()
  const [activeId, setActiveId] = useState<string | null>(null)

  const sortedIntentions = useMemo(
    () => [...intentions].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [intentions],
  )

  const sortableIds = sortedIntentions.map(intention => intention.publicId)
  const activeIntention = activeId ? sortedIntentions.find(item => item.publicId === activeId) ?? null : null
  const activePosition = activeIntention ? sortedIntentions.indexOf(activeIntention) : -1

  useLayoutEffect(() => {
    if (!tableRef.current) {
      return
    }

    const updateWidth = () => setTableWidth(tableRef.current?.offsetWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(tableRef.current)
    return () => observer.disconnect()
  }, [sortedIntentions.length])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = (event: DragStartEvent) => {
    if (isSaving) return
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    if (isSaving) return

    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = sortableIds.indexOf(String(active.id))
    const newIndex = sortableIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    const nextOrder = arrayMove(sortedIntentions, oldIndex, newIndex).map((intention, index) => ({
      ...intention,
      sortOrder: index,
    }))
    onReorder(nextOrder)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <table ref={tableRef} className="map-filters-table intentions-sort-table">
        <thead>
          <tr>
            <th className="map-filter-drag-header">Order</th>
            <th>Emoji</th>
            <th>Name</th>
            <th>Prompt</th>
            <th>Description</th>
            <th>Active</th>
            <th>Updated</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {sortedIntentions.map((intention, index) => (
              <SortableIntentionRow
                key={intention.publicId}
                intention={intention}
                position={index}
                activeCount={activeCount}
                isDisabled={isSaving}
                isToggling={togglingPublicId === intention.publicId}
                onRowClick={onRowClick}
                onActiveChange={onActiveChange}
              />
            ))}
          </SortableContext>
        </tbody>
      </table>
      <DragOverlay dropAnimation={null}>
        {activeIntention ? (
          <table
            className="map-filters-table map-filters-table-overlay"
            style={tableWidth ? { width: tableWidth } : undefined}
          >
            <tbody>
              <tr className="map-filter-row-overlay">
                <IntentionRowContent
                  intention={activeIntention}
                  position={activePosition}
                  activeCount={activeCount}
                  onRowClick={onRowClick}
                  onActiveChange={onActiveChange}
                  isOverlay
                />
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default IntentionsSortableTable
